import { gptImageSizeFor, safeZoneBaseRenderRatio } from "./aspectRatios";
import { resolveAdsExpressRenderEngine, type AdsExpressImageEngineChoice } from "./ads-express-engine-selection";
import { standardAdsExpressLegacySlotCount, adReferenceCapacityError } from "./template-photo-references";

export const MAX_STANDARD_AD_REFERENCES = 10;
export type StandardAdImageEngine = AdsExpressImageEngineChoice | "nano-banana-2";
// Provider documentation, verified 2026-10-13:
// fal.ai/models/fal-ai/gpt-image-2/edit/api (16)
// fal.ai/models/fal-ai/gpt-image-2.5-flare/edit/api (16)
// fal.ai/docs/model-api-reference/image-generation-api/nano-banana-2 (14)
// fal.ai/models/fal-ai/flux-2-pro/edit (9 references, 9 MP input)
export const STANDARD_AD_IMAGE_LIMITS: Record<StandardAdImageEngine, number> = {
  "gpt-image-2": 16, "gpt-image-2.5": 16, "nano-banana-2": 14, "flux-2-pro": 9,
};
export function standardAdReferenceCapacityError(refs: ReadonlyArray<{ purpose?: unknown }>) {
  return adReferenceCapacityError(refs, MAX_STANDARD_AD_REFERENCES);
}
export function assertStandardAdImageBudget(engine: StandardAdImageEngine, count: number, label = "This template") {
  const limit = STANDARD_AD_IMAGE_LIMITS[engine];
  if (typeof limit !== "number") {
    throw new Error(`${label} has an unsupported image engine. Choose a supported engine before generating.`);
  }
  if (count > limit) {
    throw new Error(`${label} uses ${count} image slots; ${engine} allows ${limit}. Remove ${count - limit} image slot${count - limit === 1 ? "" : "s"} from references or other selected inputs, or choose an engine with more capacity.`);
  }
}

export type StandardAdImageBudgetInput = Parameters<typeof standardAdsExpressLegacySlotCount>[0] & {
  name: string;
  ratio: string;
  isMeme?: boolean;
  userEngine?: AdsExpressImageEngineChoice | null;
  adminEngine?: "gpt-image-2" | "flux-2-pro" | null;
  identityPackEligible?: boolean;
};
function modelAttachmentInputs<T extends Parameters<typeof standardAdsExpressLegacySlotCount>[0] & { isMeme?: boolean }>(args: T): T {
  // The meme arm sends its template plus ordinary references, not the
  // standard-template logo/product/font/persona attachments.
  return args.isMeme ? {
    ...args, includeProduct: false, includeLogo: false, includeFont: false,
    includePersonaCharacterSheet: false, adIdentityPackCount: 0,
  } : args;
}
export function standardAdsExpressModelSlotCount(
  args: Parameters<typeof standardAdsExpressLegacySlotCount>[0] & { isMeme?: boolean },
) {
  return standardAdsExpressLegacySlotCount(modelAttachmentInputs(args));
}
/** Mirrors positional replacement, doubled identity and existing 16-slot pack
 * admission. Never remove an eligible identity pack to fit a smaller engine.
 * Auto is conservatively one tail slot until resolved; photos retain explicit
 * trailing-photo omission semantics, bounded by the selected provider. */
export function planStandardAdImageBudget(args: StandardAdImageBudgetInput) {
  args = modelAttachmentInputs(args);
  const ordinary = args.references.filter(ref => ref.purpose !== "template_photo");
  const photos = args.references.length - ordinary.length;
  const reservePack = !args.isMeme && args.identityPackEligible === true &&
    standardAdsExpressLegacySlotCount({ ...args, adIdentityPackCount: 3 }) <= 16;
  const legacySlots = standardAdsExpressModelSlotCount({
    ...args,
    includeFont: !args.isMeme && args.includeFont,
    adIdentityPackCount: reservePack ? 3 : 0,
  });
  const engine: StandardAdImageEngine = resolveAdsExpressRenderEngine({
    ...args,
    isMeme: args.isMeme === true,
    personIdentityActive: reservePack || ordinary.some(ref => ref.purpose === "person_identity"),
  }) ?? (gptImageSizeFor(safeZoneBaseRenderRatio(args.ratio as any)) === null ? "nano-banana-2" : "gpt-image-2");
  const limit = STANDARD_AD_IMAGE_LIMITS[engine];
  const retainedPhotos = Math.min(photos, Math.max(0, Math.min(16, limit) - legacySlots));
  const hasProductOverride = ordinary.some(ref => ref.purpose === "product" || ref.purpose === "product_environment");
  const productSlot = args.includeProduct && !!(hasProductOverride || args.primaryProductPhoto);
  const breakdown = {
    template: 1,
    logo: Number(args.includeLogo),
    product: Number(productSlot) + (args.includeProduct && !hasProductOverride ? args.extraProductPhotoCount : 0),
    font: Number(!args.isMeme && args.includeFont),
    persona: Number(args.includePersonaCharacterSheet === true) + (reservePack ? 3 : 0),
    ordinary: ordinary.reduce((sum, ref) => sum + (ref.purpose === "person_identity" ? 2 : 1), 0) -
      Number(productSlot && hasProductOverride),
    templatePhotos: retainedPhotos,
  };
  const used = legacySlots + retainedPhotos;
  let error = standardAdReferenceCapacityError(args.references);
  try { assertStandardAdImageBudget(engine, legacySlots, `${args.name} (${args.ratio})`); }
  catch (e) { error = (e as Error).message; }
  return {
    engine, limit, used, legacySlots, reservePack, retainedPhotos, breakdown,
    dropped: photos - retainedPhotos,
    remaining: Math.max(0, Math.min(MAX_STANDARD_AD_REFERENCES - ordinary.length, limit - used)),
    error,
  };
}
